import React, { useEffect, useState } from 'react';
import { FiCamera, FiBarChart2, FiCheckCircle, FiSend, FiMoon } from 'react-icons/fi';

// ─── The brand stage of the login screen ─────────────────────────────────────
// What the system actually does, played once on arrival: a handwritten
// timesheet is scanned, its rows land as clean digital shifts (a night shift
// and an overtime day among them), the month's hours count up, and the report
// goes to the worker, who signs it. Decorative only (aria-hidden). The motion
// lives in Login.css (`.sl-*`) and is off under prefers-reduced-motion.

const reducedMotion = () =>
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// Handwriting, right to left (RTL page): a wavy stroke from x0 down to x1.
function scribble(x0, x1, y, seed) {
    const parts = [`M ${x0} ${y}`];
    const steps = Math.max(2, Math.round((x0 - x1) / 9));
    for (let i = 1; i <= steps; i += 1) {
        const x = x0 - ((x0 - x1) * i) / steps;
        const lift = ((i + seed) % 3) - 1;
        const amp = 3.2 + ((i * 7 + seed) % 4) * 0.6;
        parts.push(`Q ${x + (x0 - x1) / steps / 2} ${y - amp * (lift || 1)}, ${x} ${y + lift * 0.8}`);
    }
    return parts.join(' ');
}

const ROW_Y = [92, 118, 144, 170, 196, 222, 248];
const COLS = [[214, 192], [176, 104], [72, 44]];

function PaperArt() {
    return (
        <svg className="sl-paper-svg" viewBox="0 0 240 290" aria-hidden="true" focusable="false">
            <rect x="0.5" y="0.5" width="239" height="289" rx="14" fill="#FFFDF8" stroke="#E8E2D4" />
            {/* heading + subheading, written first */}
            <path className="sl-ink" pathLength="100" style={{ animationDelay: '250ms', '--d': '520ms' }}
                d={scribble(206, 118, 30, 2)} stroke="#1E2B55" strokeWidth="2.4" />
            <path className="sl-ink" pathLength="100" style={{ animationDelay: '520ms', '--d': '420ms' }}
                d={scribble(206, 150, 46, 5)} stroke="#6B7695" strokeWidth="1.4" />
            {/* the printed grid */}
            <rect x="16" y="62" width="208" height="16" rx="4" fill="#EEF2F8" />
            {[COLS[0][1] - 6, COLS[1][1] - 12].map((x) => (
                <path key={x} d={`M ${x} 62 V 262`} stroke="#E3E7EF" strokeWidth="1" />
            ))}
            {ROW_Y.map((y) => (
                <path key={y} d={`M 16 ${y + 13} H 224`} stroke="#ECEFF5" strokeWidth="1" />
            ))}
            {/* the rows, in pen */}
            {ROW_Y.map((y, r) => COLS.map(([a, b], c) => (
                <path
                    key={`${r}-${c}`}
                    className="sl-ink"
                    pathLength="100"
                    style={{ animationDelay: `${700 + r * 120 + c * 60}ms`, '--d': '380ms' }}
                    d={scribble(a, b + ((r * 5 + c * 3) % 10), y + 4, r + c)}
                    stroke="#2B3A67"
                    strokeWidth="1.5"
                />
            )))}
            {/* the supervisor's initials at the bottom */}
            <path className="sl-ink" pathLength="100" style={{ animationDelay: '1700ms', '--d': '600ms' }}
                d="M 70 278 C 64 266, 56 268, 58 276 C 60 284, 72 280, 64 270 C 58 262, 48 282, 40 276"
                stroke="#2F72B5" strokeWidth="1.8" />
        </svg>
    );
}

const SHIFTS = [
    { d: 'א׳ 01', t: '07:00–15:30', h: '8.5' },
    { d: 'ב׳ 02', t: '07:00–16:00', h: '9.0' },
    { d: 'ג׳ 03', t: '06:30–15:00', h: '8.5' },
    { d: 'ד׳ 04', t: '22:00–06:00', h: '8.0', tag: 'night' },
    { d: 'ה׳ 05', t: '07:00–17:30', h: '10.5', tag: 'overtime' },
    { d: 'ו׳ 06', t: '07:00–12:00', h: '5.0' }
];

function useCountUp(target, { duration = 2400, delay = 1700, decimals = 1 } = {}) {
    const [value, setValue] = useState(() => (reducedMotion() ? target : 0));
    useEffect(() => {
        if (reducedMotion()) { setValue(target); return undefined; }
        let raf = 0;
        let start = 0;
        const step = (ts) => {
            if (!start) start = ts;
            const p = Math.min(1, (ts - start) / duration);
            setValue(target * (1 - (1 - p) ** 3));
            if (p < 1) raf = requestAnimationFrame(step);
        };
        const timer = setTimeout(() => { raf = requestAnimationFrame(step); }, delay);
        return () => { clearTimeout(timer); cancelAnimationFrame(raf); };
    }, [target, duration, delay]);
    return value.toFixed(decimals);
}

function ShiftArt() {
    const hours = useCountUp(186.5);
    return (
        <div className="sl-art" aria-hidden="true">
            <div className="sl-paper">
                <PaperArt />
                <div className="sl-beam" />
            </div>

            <div className="sl-digital">
                <div className="sl-digital-head">
                    <span>יומן עבודה · אוקטובר</span>
                    <span className="sl-ai-chip"><span className="sl-ai-dot" />זוהה אוטומטית</span>
                </div>
                {SHIFTS.map((s, i) => (
                    <div key={s.d} className="sl-drow" style={{ animationDelay: `${1500 + i * 380}ms` }}>
                        <span className="sl-drow-day">{s.d}</span>
                        <span className="sl-drow-time" dir="ltr">{s.t}</span>
                        {s.tag === 'night' && <span className="sl-tag is-night"><FiMoon />לילה</span>}
                        {s.tag === 'overtime' && <span className="sl-tag is-ot">125%</span>}
                        <span className="sl-drow-hours" dir="ltr">{s.h}</span>
                    </div>
                ))}
            </div>

            <div className="sl-gauge">
                <svg viewBox="0 0 100 100">
                    <defs>
                        <linearGradient id="sl-gauge-ink" x1="0" y1="0" x2="1" y2="1">
                            <stop offset="0" stopColor="#7EE0C8" />
                            <stop offset="1" stopColor="#7CC4FF" />
                        </linearGradient>
                    </defs>
                    <circle cx="50" cy="50" r="40" fill="none" stroke="rgba(255,255,255,0.14)" strokeWidth="8" />
                    <circle className="sl-gauge-arc" cx="50" cy="50" r="40" fill="none" stroke="url(#sl-gauge-ink)"
                        strokeWidth="8" strokeLinecap="round" pathLength="100" transform="rotate(-90 50 50)" />
                </svg>
                <div className="sl-gauge-text">
                    <b dir="ltr">{hours}</b>
                    <small>שעות החודש</small>
                </div>
            </div>

            <div className="sl-toast">
                <span className="sl-toast-msg is-a"><i className="sl-toast-icon"><FiSend /></i>הדוח נשלח לאישור העובד</span>
                <span className="sl-toast-msg is-b"><i className="sl-toast-icon is-ok"><FiCheckCircle /></i>העובד חתם על הדוח</span>
            </div>
        </div>
    );
}

// The phone-size stand-in for the scene: the month's hours counting up in a
// ring beside the headline (the full scene has no room above the form).
function MiniGauge() {
    const hours = useCountUp(186.5, { delay: 450, duration: 2200 });
    return (
        <div className="sl-mini" aria-hidden="true">
            <svg viewBox="0 0 100 100">
                <defs>
                    <linearGradient id="sl-mini-ink" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0" stopColor="#7EE0C8" />
                        <stop offset="1" stopColor="#7CC4FF" />
                    </linearGradient>
                </defs>
                <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.16)" strokeWidth="7" />
                <circle className="sl-gauge-arc" cx="50" cy="50" r="42" fill="none" stroke="url(#sl-mini-ink)"
                    strokeWidth="7" strokeLinecap="round" pathLength="100" transform="rotate(-90 50 50)" />
            </svg>
            <div className="sl-mini-text">
                <b dir="ltr">{hours}</b>
                <small>שעות</small>
            </div>
        </div>
    );
}

export default function LoginStage() {
    return (
        <section className="sl-stage">
            <div className="sl-stage-bg" aria-hidden="true">
                <span className="sl-orb is-a" />
                <span className="sl-orb is-b" />
                <span className="sl-orb is-c" />
            </div>

            <div className="sl-stage-top">
                <div className="sl-brand">
                    {/* The system's own icon — the same file as the favicon and the sidebar. */}
                    <img src="/assets/logo.png" alt="" className="sl-brand-mark" draggable="false" />
                    <span className="sl-brand-name">SHIFT</span>
                </div>
                <span className="sl-badge"><span className="sl-badge-dot" />ניהול כוח אדם ושכר</span>
            </div>

            <div className="sl-stage-copy">
                <h1 className="sl-headline">
                    <span>כל שעה נספרת.</span>
                    <span className="sl-headline-grad">כל שכר מדויק.</span>
                </h1>
                <p className="sl-lede">
                    סריקה אוטומטית של יומני שעות, רשומות חודש, דוחות שכר ואישור דיגיטלי של העובדים — במערכת אחת.
                </p>
            </div>

            <ShiftArt />
            <MiniGauge />

            <ul className="sl-features">
                <li><FiCamera />סריקה אוטומטית</li>
                <li><FiBarChart2 />דוחות שכר מדויקים</li>
                <li><FiCheckCircle />אישור עובד בנייד</li>
            </ul>
        </section>
    );
}
